// src/resources/customerRfm/SegmentCards.tsx
//
// Os 5 cards de segmento da tela "Segmentos de valor" (spec §6.2 item 2).
// Clicar seleciona o segmento (clicar de novo no selecionado limpa).
import React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { __t } from '../../i18n/t';
import { COLORS, cardSx, fmtBrl, fmtNum, fmtPct } from '../dashboards/shared';
import { RfmSummary } from './api';
import { RFM_SEGMENT_ORDER, SEGMENT_META, RfmSegment, deltaColor, segmentCriteria, segmentDelta } from './segments';

interface Props {
  summary: RfmSummary;
  selectedSegment?: RfmSegment;
  onSelect: (segment: RfmSegment) => void;
}

const SegmentCards: React.FC<Props> = ({ summary, selectedSegment, onSelect }) => {
  const totalValue = summary.segments.reduce((acc, s) => acc + s.totalValue, 0);

  return (
    <Grid container spacing={1.75}>
      {RFM_SEGMENT_ORDER.map((segment) => {
        const meta = SEGMENT_META[segment];
        const row = summary.segments.find((s) => s.segment === segment);
        const customers = row?.customers ?? 0;
        const value = row?.totalValue ?? 0;
        const highValueCustomers = row?.highValueCustomers ?? 0;
        const delta = segmentDelta(row);
        const selected = selectedSegment === segment;
        const pctBase = summary.total > 0 ? customers / summary.total : 0;
        const pctRevenue = totalValue > 0 ? value / totalValue : 0;
        const perCustomerYear = customers > 0 ? value / customers : 0;

        return (
          <Grid key={segment} size={{ xs: 12, sm: 6, md: 12 / 5 }}>
            <Tooltip title={segmentCriteria(segment, summary.cutoffs)} arrow>
              <Box
                onClick={() => onSelect(segment)}
                sx={{
                  ...cardSx,
                  p: 1.75,
                  cursor: 'pointer',
                  height: '100%',
                  border: `1px solid ${selected ? meta.color : 'rgba(120,105,223,0.10)'}`,
                  borderWidth: selected ? 2 : 1,
                  boxShadow: selected ? `0 6px 20px ${meta.color}33` : cardSx.boxShadow,
                  bgcolor: selected ? `${meta.color}0d` : 'rgba(255,255,255,0.9)',
                  transition: 'all 0.15s',
                  '&:hover': { boxShadow: `0 6px 20px ${meta.color}26` },
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: meta.color }}>
                  {meta.emoji} {__t(meta.label)}
                </Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.ink, mt: 0.25 }}>
                  {fmtNum(customers)}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>{fmtPct(pctBase, 1)} da base</Typography>

                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, mt: 1 }}>
                  {fmtBrl(value)}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>
                  {fmtPct(pctRevenue, 1)} do faturamento · {fmtBrl(perCustomerYear)}/cliente
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                  {delta !== null && (
                    <Tooltip title="Variação nos últimos 30 dias" arrow>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11.5, fontWeight: 700, borderRadius: 999, px: 0.9, py: 0.15,
                          color: deltaColor(segment, delta),
                          bgcolor: delta === 0 ? 'rgba(148,163,184,0.12)' : `${deltaColor(segment, delta)}1a`,
                        }}
                      >
                        {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {fmtNum(Math.abs(delta))}
                      </Box>
                    </Tooltip>
                  )}
                  {highValueCustomers > 0 && (
                    <Tooltip title="Top 20% de gasto da rede" arrow>
                      <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: COLORS.ink }}>
                        💎 {fmtNum(highValueCustomers)}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default SegmentCards;
